import React, { useEffect, useRef, useState } from "react";
import FontIcon from "react-md/lib/FontIcons";
import Tooltip from "@material-ui/core/Tooltip";
import callSession, { sessionAppointment } from "./callSession";
import callEventStream from "service/realtime/CallEventStream";
import EventService from "service/event/EventService";
import LTRService from "../service/LTRService";
import "../styles/ltrCall.scss";

const ltrService = new LTRService();

// After hang-up the pill stays as a receipt for this long, then dismisses
// itself — unless the pointer is over it.
const RECEIPT_MS = 8000;

const call = (fn, ...args) =>
  new Promise((resolve, reject) => fn(...args, resolve, reject));

function clock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

const STEPS = [
  { kind: "appointment", icon: "event", label: "Create appointment" },
  { kind: "rx", icon: "description", label: "Create Rx" },
  { kind: "action", icon: "flag", label: "Assign action" },
  { kind: "followUp", icon: "update", label: "Follow-up" },
];

/**
 * The in-call pill: from the moment a call is placed until a few seconds after
 * hang-up. It walks the doctor through the call's steps — create the
 * appointment, then the Rx, then the action and follow-up — one labelled step
 * at a time, and ends the call on the server when they hang up.
 */
function InCallPill({ history }) {
  const [session, setSession] = useState(callSession.get());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const hoverRef = useRef(false);
  const endingRef = useRef(false);

  useEffect(() => callSession.subscribe(setSession), []);

  const live = !!session && session.phase !== "ended";
  const liveCallId = live ? session.callId : null;

  // Tick the timer while a call is live.
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  // Follow the SparkTG stream for the hang-up. It cannot tell ringing from
  // talking, so it only ever ends the call; "connected" is the doctor's tap.
  useEffect(() => {
    if (!live) return undefined;
    const unsubscribe = callEventStream.subscribe((event) => {
      if (event.type === "call" && event.call && event.call.state === "ended") {
        hangUp({ fromStream: true });
      }
    });
    callEventStream.start();
    return () => {
      unsubscribe();
      callEventStream.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, liveCallId]);

  // Dismiss the receipt on its own.
  const endedCallId = session && session.phase === "ended" ? session.callId : null;
  useEffect(() => {
    if (endedCallId == null) return undefined;
    let t = setTimeout(function dismiss() {
      if (hoverRef.current) {
        t = setTimeout(dismiss, 1500);
        return;
      }
      const current = callSession.get();
      if (current && current.callId === endedCallId) callSession.clear();
    }, RECEIPT_MS);
    return () => clearTimeout(t);
  }, [endedCallId]);

  if (!session) return null;

  const connected = session.phase === "connected";
  const ended = session.phase === "ended";
  const appt = sessionAppointment(session);
  const done = {
    appointment: !!appt,
    rx: !!(appt && appt.prescriptionId),
    action: !!(appt && appt.actions && appt.actions.length),
    followUp: !!session.followUpDone,
  };
  // The step(s) on offer now. Action and follow-up open together once the Rx
  // is in; everything else waits its turn.
  const available = (kind) => {
    if (!connected) return false;
    if (kind === "appointment") return !done.appointment;
    if (kind === "rx") return done.appointment && !done.rx;
    return done.rx && !done[kind];
  };

  const elapsed = connected && session.connectedAt
    ? (now - new Date(session.connectedAt).getTime()) / 1000
    : 0;

  const markConnected = () => {
    if (session.phase !== "dialling" || busy) return;
    const connectedAt = new Date().toISOString();
    callSession.update({ phase: "connected", connectedAt });
    call(ltrService.updateCall, session.caseId, session.callId, { connected: true })
      .then((updated) => callSession.syncCase(updated))
      .catch((err) => console.error("Error marking LTR call connected:", err));
  };

  function hangUp({ fromStream } = {}) {
    const current = callSession.get();
    if (!current || current.phase === "ended" || endingRef.current) return;
    endingRef.current = true;
    setBusy(true);
    const endedAt = new Date().toISOString();
    const durationSeconds = current.connectedAt
      ? Math.round((Date.parse(endedAt) - Date.parse(current.connectedAt)) / 1000)
      : 0;
    callSession.update({ phase: "ended", endedAt, durationSeconds });
    call(ltrService.updateCall, current.caseId, current.callId, {
      ended: true,
      durationSeconds,
    })
      .then((updated) => callSession.syncCase(updated))
      .catch((err) => {
        console.error("Error ending LTR call:", err);
        EventService.emit("showError", {
          message: `The call ended but could not be saved on the case: ${
            err?.message || "please retry from the call audit"
          }`,
        });
      })
      .finally(() => {
        endingRef.current = false;
        setBusy(false);
      });
    if (fromStream) {
      EventService.emit("showError", { message: "Call ended" });
    }
  }

  const openStep = (kind) => {
    callSession.requestAction(kind);
    const target = `/miscellaneous/ltr/details/${session.caseId}`;
    const path = (history && history.location && history.location.pathname) || "";
    if (history && path !== target) history.push(target);
  };

  const stepTooltip = (kind) => {
    if (kind === "appointment" && appt) return `Appointment ${appt.appointmentId}`;
    if (kind === "rx" && appt && appt.prescriptionId) return `Rx ${appt.prescriptionId}`;
    if (kind === "action" && done.action) return "Action set";
    if (kind === "followUp" && done.followUp) return "Follow-up set";
    return STEPS.find((s) => s.kind === kind).label;
  };

  if (ended) {
    const talk = session.durationSeconds || 0;
    return (
      <div
        className="ltr-call-pill ended"
        onMouseEnter={() => (hoverRef.current = true)}
        onMouseLeave={() => (hoverRef.current = false)}
        role="status"
      >
        <span className="ltr-call-pill-phone">
          <FontIcon>call</FontIcon>
        </span>
        <span className="ltr-call-pill-who">
          <span className="ltr-call-pill-name">Call ended</span>
          <span className="ltr-call-pill-sub">{clock(talk)}</span>
        </span>
        <span className="ltr-call-pill-divider" />
        <button
          type="button"
          className="ltr-call-pill-dismiss"
          onClick={() => callSession.clear()}
        >
          <FontIcon>close</FontIcon>
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="ltr-call-pill" role="status" aria-live="polite">
      <span className="ltr-call-pill-phone">
        <FontIcon>call</FontIcon>
      </span>
      <Tooltip
        arrow
        placement="top"
        title={connected ? `${session.ltrId || ""} · open case` : "Tap when the patient answers"}
      >
        <button
          type="button"
          className="ltr-call-pill-who"
          onClick={connected ? () => openStep("view") : markConnected}
        >
          <span className="ltr-call-pill-name">
            <span className={`ltr-call-pill-dot ${connected ? "live" : "ringing"}`} />
            {session.patientName || `PID-${session.patientId}`}
          </span>
          <span className="ltr-call-pill-sub">
            {connected ? clock(elapsed) : "Ringing… · tap when answered"}
          </span>
        </button>
      </Tooltip>

      <span className="ltr-call-pill-divider" />

      {STEPS.map((step) => {
        if (done[step.kind]) {
          return (
            <Tooltip key={step.kind} arrow placement="top" title={stepTooltip(step.kind)}>
              <span className="ltr-call-pill-step done">
                <FontIcon>check</FontIcon>
              </span>
            </Tooltip>
          );
        }
        if (available(step.kind)) {
          return (
            <button
              key={step.kind}
              type="button"
              className="ltr-call-pill-cta"
              onClick={() => openStep(step.kind)}
            >
              <FontIcon>{step.icon}</FontIcon>
              {step.label}
            </button>
          );
        }
        return (
          <Tooltip
            key={step.kind}
            arrow
            placement="top"
            title={connected ? `${step.label} — after the previous step` : "Available once the call connects"}
          >
            <span className="ltr-call-pill-step locked">
              <FontIcon>{step.icon}</FontIcon>
            </span>
          </Tooltip>
        );
      })}

      <span className="ltr-call-pill-divider" />

      <Tooltip arrow placement="top" title="Hang up">
        <button
          type="button"
          className="ltr-call-pill-hangup"
          onClick={() => hangUp()}
          disabled={busy}
          aria-label="Hang up"
        >
          <FontIcon>call_end</FontIcon>
        </button>
      </Tooltip>
    </div>
  );
}

export default InCallPill;
